import React from "react";
import {
  Card as MuiCard,
  CardProps as MuiCardProps,
  CardContent,
  CardHeader,
  CardActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";

export interface CardProps extends MuiCardProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  padding?: "none" | "small" | "medium" | "large";
}

const StyledCard = styled(MuiCard)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5, // 12px (rounded-xl)
  boxShadow: theme.shadows[1], // shadow-sm
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: "all 200ms ease",
  "&:hover": {
    boxShadow: theme.shadows[3], // shadow-md
  },
}));

const paddingMap = {
  none: 0,
  small: 2, // 16px
  medium: 3, // 24px
  large: 4, // 32px
};

/**
 * Composant Card réutilisable basé sur la charte graphique
 *
 * @example
 * ```tsx
 * <Card title="Statistiques" subtitle="Aujourd'hui">
 *   <p>Contenu de la carte</p>
 * </Card>
 *
 * <Card padding="large" actions={<Button>Action</Button>}>
 *   <p>Contenu avec actions</p>
 * </Card>
 * ```
 */
export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  actions,
  padding = "medium",
  children,
  sx,
  ...props
}) => {
  const paddingValue = paddingMap[padding];

  return (
    <StyledCard sx={sx} {...props}>
      {(title || subtitle) && (
        <CardHeader
          title={title}
          subheader={subtitle}
          sx={{
            pb: paddingValue === 0 ? 0 : paddingValue - 1,
          }}
        />
      )}
      <CardContent
        sx={{
          p: paddingValue,
          "&:last-child": {
            pb: paddingValue,
          },
        }}
      >
        {children}
      </CardContent>
      {actions && (
        <CardActions
          sx={{
            p: paddingValue,
            pt: 0,
          }}
        >
          {actions}
        </CardActions>
      )}
    </StyledCard>
  );
};
